import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/theme.css';
import AppRouter from './AppRouter';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { UserProvider } from './contexts/UserContext';
import { ToolContextProvider } from './contexts/ToolContextProvider';
import { AwardProvider } from './contexts/AwardContext';
import { AdminProvider } from './contexts/AdminContext';
import { TutorialProvider } from './contexts/TutorialContext';
import TermsAndConditionsCheck from './components/TermsAndConditionsCheck';
import AccountMigrationGate from './components/AccountMigrationGate';
import { testOxworksOrganization } from './test-oxworks';
import { addUserToOxworks } from './fix-oxworks-membership';
import { findOxworksOwner } from './find-owner';

// NOTE: Amplify.configure() is called once in index.js (the entry point), not here.
// Amplify only needs to be configured once before the app renders.

// Global error handler to suppress AWS auth byteLength errors in public pages
window.addEventListener('error', (event) => {
  if (event.error && event.error.message && event.error.message.includes('byteLength')) {
    console.log('Suppressed AWS authentication error on public page');
    event.preventDefault();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('byteLength')) {
    console.log('Suppressed AWS authentication promise rejection on public page');
    event.preventDefault();
  }
});

// Make test functions available in browser console
window.testOxworks = testOxworksOrganization;
window.fixOxworks = addUserToOxworks;
window.findOwner = findOxworksOwner;

// Add debug function for user sync issues
window.debugUserSync = () => {
  console.log('🔍 Current browser storage:');
  console.log('localStorage keys:', Object.keys(localStorage));
  console.log('sessionStorage keys:', Object.keys(sessionStorage));
  console.log('Amplify-related keys:', Object.keys(localStorage).filter(k => 
    k.startsWith('amplify') || k.startsWith('aws') || k.startsWith('CognitoIdentityServiceProvider')
  ));
  console.log('To force complete reset: localStorage.clear(); sessionStorage.clear(); location.reload();');
};

// Add comprehensive reset function for debugging
window.forceCompleteReset = () => {
  console.log('🚨 FORCING COMPLETE RESET');
  localStorage.clear();
  sessionStorage.clear();
  
  // Clear cookies if possible
  document.cookie.split(";").forEach(function(c) { 
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
  });
  
  // Clear indexedDB
  if (window.indexedDB) {
    indexedDB.databases().then(databases => {
      databases.forEach(db => {
        console.log('Deleting IndexedDB:', db.name);
        indexedDB.deleteDatabase(db.name);
      });
    });
  }
  
  setTimeout(() => window.location.reload(true), 1000);
};

// Add checkpoint monitoring function
window.monitorCheckpoints = () => {
  console.log('📍 CHECKPOINT MONITORING ENABLED');
  console.log('This will log all checkpoint activities in detail');
  localStorage.setItem('checkpoint_debug', 'true');
};

function App() {
  return (
    <Authenticator.Provider>
      <UserProvider>
        <AdminProvider>
          <TutorialProvider>
            <ToolContextProvider>
              <Router>
                <OrganizationProvider>
                  <AwardProvider>
                    <TermsAndConditionsCheck>
                      {/* Inside the terms gate, so a migrated user accepts terms before being asked
                          about moving their data — two consent prompts at once reads as a fault. */}
                      <AccountMigrationGate>
                        <AppRouter />
                      </AccountMigrationGate>
                    </TermsAndConditionsCheck>
                  </AwardProvider>
                </OrganizationProvider>
              </Router>
            </ToolContextProvider>
          </TutorialProvider>
        </AdminProvider>
      </UserProvider>
    </Authenticator.Provider>
  );
}

export default App;